import { Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { getBackofficeRole } from "@/lib/admin-api";
import { useTranslation } from "react-i18next";
import { LanguageToggle } from "@/components/I18nProvider";
import { Instagram, LogOut } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

export function MagazineHeader() {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const nav = [
    { to: "/selected", label: t("nav.selected") },
    { to: "/photographers", label: t("nav.photographers") },
    { to: "/visagists", label: t("nav.visagists") },
    { to: "/themes/portraits", label: t("nav.themes") },
    { to: "/magazin", label: t("nav.magazine") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/about", label: t("nav.about") },
  ] as const;
  const [hasBackoffice, setHasBackoffice] = useState(false);
  useEffect(() => {
    let active = true;
    if (!user) { setHasBackoffice(false); return; }
    getBackofficeRole(user.id).then((role) => { if (active) setHasBackoffice(!!role); });
    return () => { active = false; };
  }, [user]);
  const showAdmin = !user || hasBackoffice;
  const adminHref = user ? "/admin" : "/login";
  const adminLabel = user ? t("nav.admin") : t("nav.login");
  async function handleLogout() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    navigate({ to: "/login", replace: true });
  }
  return (
    <header className="sticky top-0 z-40 bg-background/85 backdrop-blur border-b border-border">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
        <Link to="/" className="serif text-xl md:text-2xl tracking-tight">
          OM<span className="text-muted-foreground"> </span>Selected
        </Link>
        <nav className="hidden md:flex items-center gap-6 lg:gap-8 eyebrow">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              className="text-foreground/80 hover:text-foreground transition-colors whitespace-nowrap"
              activeProps={{ className: "text-foreground" }}
            >
              {n.label}
            </Link>
          ))}
        </nav>
        <div className="hidden md:flex flex-wrap items-center gap-x-3 gap-y-2">
          {user && (
            <Link to="/account/profile" className="eyebrow text-muted-foreground hover:text-foreground whitespace-nowrap">
              {t("nav.profile")}
            </Link>
          )}
          {showAdmin && (
            <Link to={adminHref} className="eyebrow text-muted-foreground hover:text-foreground whitespace-nowrap">
              {adminLabel}
            </Link>
          )}
          {user && (
            <button onClick={handleLogout} aria-label={t("nav.logout")} className="text-muted-foreground hover:text-foreground transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          )}
          {!user && (
            <Link to="/apply" className="eyebrow border border-foreground/30 px-4 py-2 hover:border-foreground hover:bg-muted transition-colors whitespace-nowrap">
              {t("nav.apply")}
            </Link>
          )}
          <Link to="/brief" className="eyebrow border border-foreground px-4 py-2 hover:bg-foreground hover:text-background transition-colors whitespace-nowrap">
            {t("nav.brief")}
          </Link>
          <a
            href="https://instagram.com/om.selected"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Instagram"
            className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors"
          >
            <Instagram className="w-4 h-4" />
          </a>
          <LanguageToggle className="ml-2" />
        </div>
        <button
          aria-label={t("nav.menu")}
          className="md:hidden eyebrow"
          onClick={() => setOpen((o) => !o)}
        >
          {open ? t("nav.close") : t("nav.menu")}
        </button>
      </div>
      {open && (
        <div className="md:hidden border-t border-border bg-background">
          <nav className="flex flex-col px-5 py-4 gap-4 eyebrow">
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)}>
                {n.label}
              </Link>
            ))}
            {user && (
              <Link to="/account/profile" onClick={() => setOpen(false)} className="text-muted-foreground">
                {t("nav.profile")}
              </Link>
            )}
            {showAdmin && (
              <Link to={adminHref} onClick={() => setOpen(false)} className="text-muted-foreground">
                {adminLabel}
              </Link>
            )}
            {user && (
              <button onClick={() => { setOpen(false); handleLogout(); }} aria-label={t("nav.logout")} className="text-left text-muted-foreground inline-flex items-center gap-2">
                <LogOut className="w-4 h-4" />
                {t("nav.logout")}
              </button>
            )}
            {!user && (
              <Link to="/apply" onClick={() => setOpen(false)} className="border border-foreground/30 px-4 py-2 inline-block w-fit">
                {t("nav.apply")}
              </Link>
            )}
            <Link to="/brief" onClick={() => setOpen(false)} className="border border-foreground px-4 py-2 inline-block w-fit">
              {t("nav.brief")}
            </Link>
            <a
              href="https://instagram.com/om.selected"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-muted-foreground"
              onClick={() => setOpen(false)}
            >
              <Instagram className="w-4 h-4" />
              Instagram
            </a>
            <LanguageToggle className="mt-2" />
          </nav>
        </div>
      )}
    </header>
  );
}